import InteractiveChatBox from "@components/InteractiveChat/components/InteractiveChatBox/InteractiveChatBox";
import SourcesToast from "@components/SourcesToast/SourcesToast";
import { ARA_LOGIN_URL, CONVERSATION_RETENTION_DAYS } from "@config";
import ROUTES from "@constants/routing";
import { UserContext } from "@context/User/UserContext";
import { useUserProfile } from "@hooks/useUserProfile";
import i18n, { getLngCode } from "@i18n/i18n";
import { Box } from "@mui/material";
import {
  chatStreamingService,
  AuthenticationError as ChatAuthenticationError
} from "@services/chatStreamingApi";
import {
  conversationService,
  AuthenticationError as ConversationAuthenticationError
} from "@services/conversations";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";

import {
  AvatarChatHistoryMessageSource,
  IAvatarChatHistoryMessage
} from "./components/InteractiveChatHistory";
import InteractiveChatSettings from "./components/InteractiveChatSettings";

export interface InteractiveChatProps {
  onRequestLogin?: () => void;
  initialMessage?: string;
  initialMessageDisplay?: string;
  initialMessageBackend?: string;
  initialAudio?: Blob | null;
  threadId?: string;
}

export const InteractiveChat = ({
  onRequestLogin,
  initialMessage,
  initialMessageDisplay,
  initialMessageBackend,
  initialAudio,
  threadId
}: InteractiveChatProps) => {
  const { t } = useTranslation();
  const userCtx = useContext(UserContext);
  const { refetch: refetchUserProfile } = useUserProfile();

  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const [chatHistory, setChatHistory] = useState<IAvatarChatHistoryMessage[]>(
    []
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [conversationCreatedAt, setConversationCreatedAt] =
    useState<Date | null>(null);
  const [sourcesToastOpen, setSourcesToastOpen] = useState(false);
  const [currentSources, setCurrentSources] = useState<string[]>([]);
  const processedInitialMessageRef = useRef<string | null>(null);
  const processedInitialAudioRef = useRef<{
    size: number;
    type: string;
  } | null>(null);

  // Calculate if conversation is expired (memoized to recalculate when timestamp changes)
  const isConversationExpired = useMemo((): boolean => {
    if (!conversationCreatedAt || !threadId) {
      return false;
    }
    const now = new Date();
    const daysDiff =
      (now.getTime() - conversationCreatedAt.getTime()) / (1000 * 60 * 60 * 24);
    const expired = daysDiff > CONVERSATION_RETENTION_DAYS;

    return expired;
  }, [conversationCreatedAt, threadId]);

  // Helper function to check if user session is valid
  const validateUserSession = async (): Promise<boolean> => {
    try {
      const result = await refetchUserProfile();
      return result.isSuccess && !!result.data;
    } catch {
      return false;
    }
  };

  // Helper function to handle authentication errors
  const handleAuthenticationError = () => {
    const currentUrl = encodeURIComponent(window.location.href);
    window.location.href = `${ARA_LOGIN_URL}${currentUrl}`;
  };

  const startSession = useCallback(() => {
    // If we have a threadId, use it as sessionId; otherwise generate a new one
    if (threadId) {
      setSessionId(threadId);
    } else {
      setSessionId(chatStreamingService.generateSessionId());
    }
  }, [threadId]);

  const addMessageToHistory = (
    text: string,
    source: AvatarChatHistoryMessageSource,
    isComplete: boolean = true
  ) => {
    const message: IAvatarChatHistoryMessage = {
      source,
      text,
      timestamp: new Date(),
      isComplete
    };
    setChatHistory((prev: IAvatarChatHistoryMessage[]) => [...prev, message]);
  };

  const updateLastBotMessage = (text: string, isComplete: boolean = false) => {
    setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
      const newHistory = [...prev];
      const lastIndex = newHistory.length - 1;
      if (
        lastIndex >= 0 &&
        newHistory[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
      ) {
        newHistory[lastIndex] = { ...newHistory[lastIndex], text, isComplete };
      }
      return newHistory;
    });
  };

  const getCoordinatesIfAllowed = useCallback(async () => {
    if (userCtx?.locationPermission !== "granted") {
      return undefined;
    }

    const permissionState =
      await chatStreamingService.checkGeolocationPermission();

    // Try to get coordinates if permission is granted, prompt, or unknown
    // (prompt/unknown can happen on mobile even after user granted permission)
    if (
      permissionState === "granted" ||
      permissionState === "prompt" ||
      permissionState === "unknown"
    ) {
      try {
        const coordinates = await Promise.race([
          chatStreamingService.getCurrentLocation(),
          new Promise<undefined>(
            (resolve) => setTimeout(() => resolve(undefined), 8500) // 8.5s, less than browser's 10s
          )
        ]);
        return coordinates;
      } catch (error) {
        console.warn("Error getting location:", error);
        return undefined;
      }
    } else if (permissionState === "denied") {
      // Browser permission was revoked, update our state
      userCtx.setLocationPermission("denied");
      return undefined;
    }

    return undefined;
  }, [userCtx]);

  // Helper function to create streaming callbacks
  const createStreamCallbacks = () => ({
    onMetadata: (sources: string[]) => {
      // Add sources to the last avatar message and mark as complete
      setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (
          lastIndex >= 0 &&
          updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
        ) {
          updated[lastIndex] = {
            ...updated[lastIndex],
            sources,
            isComplete: true // Mark as complete when sources arrive
          };
        }
        return updated;
      });
    },
    onToken: (token: string) => {
      setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (
          lastIndex >= 0 &&
          updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
        ) {
          const currentText = updated[lastIndex].text || "";
          const thinkingText = t("chatbot.actions.thinking");
          // If the current text is "Un segundo..." or empty, replace it with the token
          // Otherwise, append the token
          const newText =
            currentText === thinkingText || currentText === ""
              ? token
              : currentText + token;
          updated[lastIndex] = {
            ...updated[lastIndex],
            text: newText,
            isComplete: false
          };
        }
        return updated;
      });
    },
    onTranscription: (transcription: string) => {
      // Update the placeholder message with the actual transcription
      setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
        const updated = [...prev];

        // Find the last USER message (not just the last message in general)
        let userMessageIndex = -1;
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].source === AvatarChatHistoryMessageSource.USER) {
            userMessageIndex = i;
            break;
          }
        }

        if (userMessageIndex >= 0) {
          updated[userMessageIndex] = {
            ...updated[userMessageIndex],
            text: transcription
          };
        }
        return updated;
      });
    },
    onError: (_error: string) => {
      updateLastBotMessage(t("chatbot.errors.connection_error"), true);
    }
  });

  const streamResponse = async (streamPromise: Promise<any>) => {
    setIsLoading(true);
    addMessageToHistory(
      t("chatbot.actions.thinking"),
      AvatarChatHistoryMessageSource.AVATAR,
      false
    );
    try {
      await streamPromise;
      // Mark message as complete when streaming finishes
      setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (
          lastIndex >= 0 &&
          updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR
        ) {
          updated[lastIndex] = {
            ...updated[lastIndex],
            isComplete: true
          };
        }
        return updated;
      });

      // Persist session in URL so the chat survives page reloads.
      // Uses replaceState to avoid triggering React Router re-renders.
      if (sessionId && !threadId) {
        window.history.replaceState(
          null,
          "",
          `${ROUTES.CHATBOT}?thread_id=${sessionId}`
        );
      }
    } catch (error) {
      if (chatStreamingService.isCancellationError(error)) {
        return;
      }

      console.error("Error durante el streaming:", error);

      // Handle authentication errors
      if (error instanceof ChatAuthenticationError) {
        const errorMessage = t("chatbot.errors.session_expired");
        updateLastBotMessage(errorMessage, true);
        // Redirect to login after a short delay
        setTimeout(() => {
          handleAuthenticationError();
        }, 2000);
        return;
      }

      const errorText = t("chatbot.errors.connection_error");
      updateLastBotMessage(errorText, true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextMessage = async () => {
    if (!text || !sessionId) return;

    // Validate user session before sending message
    const isSessionValid = await validateUserSession();
    if (!isSessionValid) {
      updateLastBotMessage(t("chatbot.errors.session_expired"), true);
      handleAuthenticationError();
      return;
    }

    const currentText = text;
    addMessageToHistory(currentText, AvatarChatHistoryMessageSource.USER);
    setText("");

    const coordinates = await getCoordinatesIfAllowed();
    const languageCode = getLngCode(i18n.language);

    await streamResponse(
      chatStreamingService.streamChat(
        currentText,
        sessionId,
        languageCode,
        coordinates || undefined,
        createStreamCallbacks()
      )
    );
  };

  const handleVoiceMessage = async (audioBlob: Blob) => {
    if (!audioBlob || !sessionId) return;

    // Validate user session before sending message
    const isSessionValid = await validateUserSession();
    if (!isSessionValid) {
      updateLastBotMessage(t("chatbot.errors.session_expired"), true);
      handleAuthenticationError();
      return;
    }

    addMessageToHistory(
      t("chatbot.common.voice_message_placeholder", "[Mensaje de voz]"),
      AvatarChatHistoryMessageSource.USER
    );

    const coordinates = await getCoordinatesIfAllowed();
    const languageCode = getLngCode(i18n.language);

    await streamResponse(
      chatStreamingService.streamChatWithAudio(
        audioBlob,
        sessionId,
        languageCode,
        coordinates || undefined,
        createStreamCallbacks()
      )
    );

    // Clear audio blob after processing
    setAudioBlob(null);
  };

  const handleRecord = async (blob: Blob) => {
    setAudioBlob(blob);
    await handleVoiceMessage(blob);
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
  };

  const handleStop = () => {
    chatStreamingService.cancelCurrentStream();
    setIsLoading(false);
    // Remove the "thinking" message if it exists
    setChatHistory((prev: IAvatarChatHistoryMessage[]) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (
        lastIndex >= 0 &&
        updated[lastIndex].source === AvatarChatHistoryMessageSource.AVATAR &&
        updated[lastIndex].text === t("chatbot.actions.thinking")
      ) {
        return updated.slice(0, lastIndex);
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    await handleTextMessage();
  };

  // Reset chat and load history when threadId changes
  useEffect(() => {
    // Reset processed refs when threadId changes
    processedInitialMessageRef.current = null;
    processedInitialAudioRef.current = null;

    if (!threadId) {
      // New chat: clear history and generate new session ID
      if (sessionId) {
        setChatHistory([]);
        setConversationCreatedAt(null);
        setSessionId(chatStreamingService.generateSessionId());
      }
      return;
    }

    // Existing thread: update sessionId to match threadId and load history
    if (sessionId !== threadId) {
      setSessionId(threadId);
    }

    // Load conversation history
    const loadConversationHistory = async () => {
      if (!userCtx?.user) {
        return;
      }

      setIsLoadingHistory(true);
      // Clear previous history immediately when threadId changes
      setChatHistory([]);

      try {
        const userId = userCtx.user.userId?.toString();
        const history = await conversationService.getConversationHistory(
          threadId,
          userId
        );
        const messages = conversationService.convertToChatHistory(history);
        setChatHistory(messages);

        // Store conversation creation timestamp (use first message timestamp or created_at)
        let createdAt: Date | null = null;

        if (history.created_at) {
          const parsedDate = new Date(history.created_at);
          if (!isNaN(parsedDate.getTime())) {
            createdAt = parsedDate;
          }
        }

        // Fallback to first message timestamp if created_at is not available or invalid
        if (!createdAt && messages.length > 0 && messages[0].timestamp) {
          createdAt =
            messages[0].timestamp instanceof Date
              ? messages[0].timestamp
              : new Date(messages[0].timestamp);
        }

        if (createdAt) {
          setConversationCreatedAt(createdAt);
          // Debug log (can be removed later)
        }
      } catch (error) {
        console.error("Error loading conversation history:", error);
        // Handle authentication errors
        if (error instanceof ConversationAuthenticationError) {
          const errorMessage = t(error.translationKey);
          setChatHistory([
            {
              source: AvatarChatHistoryMessageSource.AVATAR,
              text: errorMessage,
              timestamp: new Date(),
              isComplete: true
            }
          ]);
          // Redirect to login after a short delay
          setTimeout(() => {
            handleAuthenticationError();
          }, 2000);
          return;
        }
        // On error, show welcome message only if we have no history
        const errorMessage =
          error instanceof Error && error.message.startsWith("chatbot.errors.")
            ? t(error.message)
            : t("chatbot.errors.unknown_error");
        setChatHistory([
          {
            source: AvatarChatHistoryMessageSource.AVATAR,
            text: errorMessage,
            timestamp: new Date(),
            isComplete: true
          }
        ]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadConversationHistory();
    // Only depend on threadId and user, not on chatHistory or sessionId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, userCtx?.user?.userId]);

  useEffect(() => {
    if (!sessionId) {
      startSession();
    }
  }, [sessionId, startSession]);

  useEffect(() => {
    // Only process initial message/audio once per unique value
    // Use display version for showing, backend version for sending
    const messageToDisplay =
      initialMessageDisplay !== undefined
        ? initialMessageDisplay
        : initialMessage;
    const messageToSend =
      initialMessageBackend !== undefined
        ? initialMessageBackend
        : initialMessage;

    if (
      sessionId &&
      messageToDisplay &&
      messageToDisplay !== processedInitialMessageRef.current
    ) {
      processedInitialMessageRef.current = messageToDisplay;
      // Show the display version in chat history
      addMessageToHistory(
        messageToDisplay,
        AvatarChatHistoryMessageSource.USER
      );
      (async () => {
        const coordinates = await getCoordinatesIfAllowed();
        const languageCode = getLngCode(i18n.language);
        // Send the backend version to the API
        await streamResponse(
          chatStreamingService.streamChat(
            messageToSend || messageToDisplay,
            sessionId,
            languageCode,
            coordinates || undefined,
            createStreamCallbacks()
          )
        );
      })();
    } else if (
      sessionId &&
      initialAudio &&
      (!processedInitialAudioRef.current ||
        processedInitialAudioRef.current.size !== initialAudio.size ||
        processedInitialAudioRef.current.type !== initialAudio.type)
    ) {
      processedInitialAudioRef.current = {
        size: initialAudio.size,
        type: initialAudio.type
      };
      handleVoiceMessage(initialAudio);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, initialMessage, initialAudio]);

  useEffect(() => {
    // Only show welcome message if:
    // - We have a session
    // - No initial message or audio
    // - No chat history (either empty or not loaded yet)
    // - Not loading history (to avoid showing welcome while loading)
    // - No threadId (if threadId exists, we're loading a conversation)
    const hasInitialMessage =
      initialMessage ||
      (initialMessageDisplay !== undefined && initialMessageDisplay);
    if (
      sessionId &&
      !hasInitialMessage &&
      !initialAudio &&
      chatHistory.length === 0 &&
      !isLoadingHistory &&
      !threadId
    ) {
      addMessageToHistory(
        t("chatbot.common.welcome_message"),
        AvatarChatHistoryMessageSource.AVATAR
      );
    }
  }, [
    sessionId,
    initialMessage,
    initialAudio,
    chatHistory.length,
    isLoadingHistory,
    threadId
  ]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {!sessionId ? (
        <InteractiveChatSettings
          startSession={startSession}
          onRequestLogin={onRequestLogin}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            maxWidth: { xs: "100%", sm: 800 },
            mx: "auto",
            width: "100%"
          }}
        >
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <InteractiveChatBox
              messages={chatHistory}
              disabled={
                !sessionId ||
                isLoading ||
                isConversationExpired ||
                isLoadingHistory
              }
              loading={isLoading}
              text={text}
              setText={setText}
              handleSubmitMessage={handleSubmit}
              handleRecord={handleRecord}
              audioBlob={audioBlob}
              handleDeleteAudio={handleDeleteAudio}
              handleStop={handleStop}
              isExpired={isConversationExpired}
              expirationDays={CONVERSATION_RETENTION_DAYS}
              onSourcesClick={(sourceIds) => {
                setCurrentSources(sourceIds);
                setSourcesToastOpen(true);
              }}
            />
          </Box>
        </Box>
      )}

      {/* Sources Toast */}
      <SourcesToast
        open={sourcesToastOpen}
        onClose={() => setSourcesToastOpen(false)}
        sourceIds={currentSources}
      />
    </Box>
  );
};

export default InteractiveChat;
